<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import WgptCustomReportsAPI from 'dashboard/api/wgpt/customReports';
import { useReportDrilldown } from 'dashboard/routes/dashboard/settings/reports/composables/useReportDrilldown';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import SidePanel from 'dashboard/components-next/side-panel/SidePanel.vue';
import CustomEventDrilldownCard from './CustomEventDrilldownCard.vue';

const props = defineProps({
  open: { type: Boolean, default: false },
  reportId: { type: [String, Number], default: null },
  title: { type: String, default: '' },
  // The clicked bucket (period and/or group) and the value the chart showed for it.
  bucketLabel: { type: String, default: '' },
  bucketValue: { type: String, default: '' },
  // The report query the bucket came from, narrowed to it (see `buildDrilldownParams`).
  params: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['close']);

const panelRef = ref(null);
const { t } = useI18n();
const {
  records,
  meta,
  isFetching,
  isFetchingMore,
  hasError,
  hasRecords,
  hasMore,
  open: openDrilldown,
  close,
  loadMore,
} = useReportDrilldown(request => WgptCustomReportsAPI.drilldown(request));

const subtitle = computed(() =>
  [
    props.bucketLabel,
    meta.value.total_count
      ? t('WGPT_CUSTOM_REPORTS.DRILLDOWN.RESULT_COUNT', {
          count: meta.value.total_count,
        })
      : '',
  ]
    .filter(Boolean)
    .join(' ⋅ ')
);

const fetchDrilldown = () => {
  openDrilldown({ reportId: props.reportId, params: props.params });
};

watch(
  () => props.open,
  isDrawerOpen => {
    if (!isDrawerOpen) {
      panelRef.value?.close();
      close();
      return;
    }

    panelRef.value?.open();
    fetchDrilldown();
  }
);

watch(
  () => props.params,
  () => {
    if (props.open) fetchDrilldown();
  }
);
</script>

<template>
  <SidePanel ref="panelRef" :title="title" width="xl" @close="emit('close')">
    <template #header>
      <div class="min-w-0">
        <h3 class="truncate text-base font-medium text-n-slate-12">
          {{ title }}
        </h3>
        <p
          v-if="bucketValue"
          class="mt-1 text-xl font-semibold text-n-slate-12"
        >
          {{ bucketValue }}
        </p>
        <div
          class="text-sm text-n-slate-11"
          :class="bucketValue ? 'mt-2' : 'mt-1'"
        >
          {{ subtitle }}
        </div>
      </div>
    </template>

    <div v-if="isFetching" class="flex h-40 items-center justify-center">
      <Spinner />
    </div>

    <div
      v-else-if="hasError"
      class="flex h-40 items-center justify-center text-sm text-n-ruby-11"
    >
      {{ t('WGPT_CUSTOM_REPORTS.DRILLDOWN.ERROR') }}
    </div>

    <div
      v-else-if="!hasRecords"
      class="flex h-40 items-center justify-center text-sm text-n-slate-10"
    >
      {{ t('WGPT_CUSTOM_REPORTS.DRILLDOWN.EMPTY') }}
    </div>

    <div v-else class="flex flex-col gap-2">
      <CustomEventDrilldownCard
        v-for="event in records"
        :key="event.id"
        :event="event"
        :event-name="meta.event_name"
      />

      <Button
        v-if="hasMore"
        faded
        slate
        size="sm"
        class="mx-auto mt-2"
        :label="t('WGPT_CUSTOM_REPORTS.DRILLDOWN.LOAD_MORE')"
        :is-loading="isFetchingMore"
        @click="loadMore"
      />
    </div>
  </SidePanel>
</template>
